/**
 * Step 2 — meeting method.
 *
 * Implemented with `<fieldset><legend>` + 3 real `<input type="radio">` so screen
 * readers announce it as a single grouped choice (rcp-security B.5 fix).
 * Visual selection keeps a check badge + border + tint (not color-only).
 */

import type { Dispatch } from 'react';
import type { BookingAction } from '../wizardState';
import type { MeetingMethod, BookingWizardState } from '../types';
import { Button } from '@/components/primitives';

const METHODS: ReadonlyArray<{ value: MeetingMethod; icon: string; title: string; micro: string; badge: string }> = [
  { value: 'inPerson', icon: '🏢', title: 'In Person', micro: 'Meet at one of our Phelps County branches.', badge: 'Most popular' },
  { value: 'phone', icon: '📞', title: 'Phone', micro: "We'll call you at the time you pick.", badge: 'No travel' },
  { value: 'video', icon: '📹', title: 'Video', micro: "We'll email you a Microsoft Teams link.", badge: 'Screen sharing' },
];

export interface Step2MethodProps {
  state: BookingWizardState;
  dispatch: Dispatch<BookingAction>;
  onContinue: () => void;
  onBack: () => void;
}

export function Step2Method({ state, dispatch, onContinue, onBack }: Step2MethodProps): JSX.Element {
  return (
    <main className="rae-wizard__main">
      <button type="button" className="rae-wizard__back" onClick={onBack}>← Back</button>
      <h1 className="rae-wizard__h1">How would you like to meet?</h1>
      <p className="rae-wizard__lead">
        Choose what works best for you. You can change this later — up to 4 hours before your appointment.
      </p>

      <fieldset className="rae-methods">
        <legend>Meeting method</legend>
        {METHODS.map((m) => {
          const isSelected = state.method === m.value;
          return (
            <label
              key={m.value}
              className={`rae-method ${isSelected ? 'is-selected' : ''}`.trim()}
            >
              <input
                type="radio"
                name="rae-method"
                value={m.value}
                checked={isSelected}
                onChange={() => dispatch({ type: 'setMethod', method: m.value })}
              />
              <span className="rae-method__check" aria-hidden>✓</span>
              <span className="rae-method__icon" aria-hidden>{m.icon}</span>
              <span className="rae-method__title">{m.title}</span>
              <span className="rae-method__micro">{m.micro}</span>
              <span className="rae-method__badge">{m.badge}</span>
            </label>
          );
        })}
      </fieldset>

      <div className="rae-wizard__actions">
        <Button variant="ghost" size="lg" onClick={onBack}>← Back</Button>
        <Button
          variant="primary"
          size="lg"
          disabled={!state.method}
          onClick={onContinue}
        >
          Continue →
        </Button>
      </div>
    </main>
  );
}
